/*
 * fyre-fitment-data.js — shared client-side loaders for fitment data.
 *
 * FitmentData        — loads fitment-vehicles.json (19k+ vehicles for dropdowns)
 * FitmentCollections — queries Storefront API at runtime to map vehicle → collection
 * FitmentNameplate   — nameplate matcher: routes a contested vehicle/query to the
 *                      collection whose make/model it actually names
 *
 * FitmentData/FitmentCollections are memoised; multiple sections on the same page
 * share one fetch. FitmentNameplate is pure and stateless.
 */
(function () {
  if (!window.FitmentNameplate) {
    /*
     * Compares slugified name tokens so a vehicle (or a free-text search query) is
     * routed to the collection whose nameplate it matches — not whichever collection
     * happened to enumerate first. WHY: mixed-nameplate fitment groups (172 of 505
     * groups hold >1 nameplate, e.g. group-1669 = "Cadillac XLR / Chevrolet Corvette")
     * make a single vehicle claimable by multiple collections; token matching picks the
     * right one. Shared by FitmentCollections (below) and fyre-predictive-search.js.
     */
    function slugTokens(str) {
      return String(str || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .split(' ')
        // keep multi-char tokens and single DIGITS (model numbers: BMW "3" Series, Mazda
        // "6"); drop single letters + empties. WHY: dropping "3" would collapse BMW 3 Series
        // and 1/2 Series to the same "bmw series" nameplate and mis-route between them.
        .filter(function (t) { return t.length > 1 || /[0-9]/.test(t); });
    }
    // Vehicle handles are "<year>-<make>-<model>[-<submodel>]" slugified; drop the leading
    // 4-digit year so only make/model/submodel remain as nameplate tokens.
    function vehicleTokens(handle) {
      return slugTokens(handle).filter(function (t) { return !/^\d{4}$/.test(t); });
    }
    function firstYear(str) {
      var m = String(str || '').match(/(?:19|20)\d{2}/);
      return m ? parseInt(m[0], 10) : null;
    }
    // Parse a "2005-2013" style span from a collection title, if present (en/em dash ok).
    function yearRange(title) {
      var m = String(title || '').match(/((?:19|20)\d{2})\s*[-–—]\s*((?:19|20)\d{2})/);
      return m ? { from: parseInt(m[1], 10), to: parseInt(m[2], 10) } : null;
    }
    // Count how many of `tokens` appear in the collection's slugified handle+title.
    function scoreCollection(tokens, coll) {
      var hay = slugTokens((coll.handle || '') + ' ' + (coll.title || ''));
      var n = 0;
      for (var i = 0; i < tokens.length; i++) {
        if (hay.indexOf(tokens[i]) !== -1) n++;
      }
      return n;
    }
    window.FitmentNameplate = {
      tokens: slugTokens,
      score: scoreCollection,
      /*
       * Conditional routing: return a collection handle ONLY when we are
       * confident it names this vehicle — the vehicle's make AND model tokens both appear
       * in the collection handle+title AND the collection's year span (if the title has one)
       * covers the vehicle year. Otherwise return null so the caller routes to search +
       * fitment facet, never to a wrong-nameplate sibling page. Among confident candidates
       * the highest token score wins (most specific generation). Year is a VETO here, not a
       * tiebreak — a "2015-2021" collection can't claim a 2005 vehicle however many name
       * tokens it shares.
       */
      pickCollection: function (vehicleHandle, candidates) {
        var vTokens = vehicleTokens(vehicleHandle);
        var vYear = firstYear(vehicleHandle);
        // handle = <year>-<make>-<model>[-<submodel>]. Two-word makes (Mercedes-Benz, Land Rover,
        // Alfa Romeo, Aston Martin) carry the model at index 2 — index 1 is the make's second word
        // and would trivially match every same-make collection (routing a GL to an "ML/GLE" page).
        // For them match make as both words and the model by prefix (ml500 ~ ml) so ML500 -> ML/GLE
        // maps while GL450 -> search. Single-word makes use exact model matching.
        var TWOWORD = { mercedes: 1, land: 1, alfa: 1, aston: 1 };
        var twoWord = !!TWOWORD[vTokens[0]];
        var vMake = twoWord ? [vTokens[0], vTokens[1]] : [vTokens[0]];
        var vModel = twoWord ? vTokens[2] : vTokens[1];
        var best = null, bestScore = -1;
        for (var i = 0; i < candidates.length; i++) {
          var c = candidates[i];
          var hay = slugTokens((c.handle || '') + ' ' + (c.title || ''));
          var range = yearRange(c.title);
          var yearOk = !range || !vYear || (vYear >= range.from && vYear <= range.to);
          var makeOk = vMake.every(function (m) { return m && hay.indexOf(m) !== -1; });
          var modelOk;
          if (!vModel) modelOk = true;
          else if (twoWord) modelOk = hay.some(function (t) { return t.length >= 2 && vModel.indexOf(t) === 0; });
          else modelOk = hay.indexOf(vModel) !== -1;
          if (!(yearOk && makeOk && modelOk)) continue; // not confident -> leave to search fallback
          var score = scoreCollection(vTokens, c);
          if (score > bestScore) { best = c; bestScore = score; }
        }
        return best ? best.handle : null;
      }
    };
  }

  if (!window.FitmentData) {
    // Keyed by URL, not a single slot: the vehicle list is served as one file per Type, so a
    // single-slot memo would hand the Moto catalogue back for an Auto request.
    var _byUrl = {};
    /*
     * Rehydrate the v2 wire format into the object shape every caller already expects.
     *
     * v2 nests by type, interns make/model, drops the repeated keys, and omits `display` — which
     * is exactly year+make+model+submodel and costs a megabyte to ship 23,196 times. Rebuilding
     * it here is one string concat per row and keeps all 43 `.display` reads working untouched.
     *
     * A plain array is still accepted, so a cached v1 asset served during a CDN sync lag
     * degrades to the old behaviour rather than emptying every dropdown.
     */
    function rehydrate(d) {
      if (Array.isArray(d)) return d;
      if (!d || d.v !== 2) return [];
      var mk = d.mk || [], md = d.md || [], out = [];
      function push(rows, type) {
        for (var i = 0; i < rows.length; i++) {
          var r = rows[i];
          var make = r[2] >= 0 ? mk[r[2]] : null;
          var model = r[3] >= 0 ? md[r[3]] : null;
          var year = r[1] ? String(r[1]) : null;
          var sub = r[4] || '';
          var e = { id: r[0], type: type, make: make, model: model, submodel: sub, year: year };
          e.display = [year, make, model, sub].filter(Boolean).join(' ');
          if (r[5]) e.gid = r[5];
          out.push(e);
        }
      }
      push(d.auto || [], 'Auto');
      push(d.moto || [], 'Moto');
      return out;
    }
    /*
     * How many REAL requests one page view may spend on a URL that keeps answering empty.
     * Mirrors fyre-fitment.js loadUrl — this module is the fallback loader the PDP uses when
     * FyreFitment is absent, and until now only the primary dropped its memo on an empty
     * answer: a 200 carrying `[]` here stayed memoised, so the PDP's one-shot retry through
     * this path was a pure cache read that issued no request while burning the one attempt.
     * The bound stops the drop turning a permanently mis-built asset into a request storm.
     */
    var MAX_EMPTY_ATTEMPTS = 3;
    var _emptyAttempts = {};
    function noteEmpty(url) {
      _emptyAttempts[url] = (_emptyAttempts[url] || 0) + 1;
      if (_emptyAttempts[url] >= MAX_EMPTY_ATTEMPTS) return false;
      delete _byUrl[url];
      return true;
    }
    function load(url) {
      if (!url) return Promise.resolve([]);
      if (_byUrl[url]) return _byUrl[url];
      _byUrl[url] = fetch(url)
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(rehydrate)
        .then(function (rows) {
          if (!rows || !rows.length) noteEmpty(url);
          else delete _emptyAttempts[url];
          return rows || [];
        })
        // Unbounded, mirroring fyre-fitment.js: a rejected fetch is 503/DNS/abort as often as
        // 404, and those recover — counting them would let a brief outage memoise the catalogue
        // as empty for the whole page view with no further request ever issued.
        .catch(function () { delete _byUrl[url]; return []; });
      return _byUrl[url];
    }

    window.FitmentData = {
      TYPES: ['Auto', 'Moto'],
      rehydrate: rehydrate,
      load: load,
      /*
       * Load only the catalogue the shopper asked for. Type is the first dropdown and its two
       * values are fixed, so it renders with no data at all and the fetch is deferred until a
       * Type is chosen — an automotive shopper never downloads the motorcycles.
       *
       * `el` is the element carrying data-vehicle-src-auto / -moto / -src. A theme still serving
       * the pre-split markup has only the combined attribute, so it falls back to that rather
       * than emptying every dropdown.
       */
      srcFor: function (el, type) {
        var d = (el && el.dataset) || {};
        if (type === 'Moto' && d.vehicleSrcMoto) return d.vehicleSrcMoto;
        if (type === 'Auto' && d.vehicleSrcAuto) return d.vehicleSrcAuto;
        return d.vehicleSrc || '';
      },
      loadType: function (el, type) { return load(window.FitmentData.srcFor(el, type)); }
    };
  }

  if (!window.FitmentCollections) {
    var _map = null;
    var _groupMap = {};
    window.FitmentCollections = {
      /*
       * Builds vehicle handle → collection handle map via Storefront API.
       * Query: collections → fitment.groups metafield → each group's
       * vehicles references → vehicle handles (= vehicle.id in fitment-vehicles.json).
       * NOTE: the namespace must be fitment/groups — custom.fitment_groups does not exist
       * and returns 0 collections (empty map → blank dropdown).
       */
      load: function (storefrontUrl, token) {
        if (_map) return _map;
        if (!storefrontUrl || !token) return Promise.resolve({});
        var query = [
          '{',
          '  collections(first: 250) {',
          '    nodes {',
          '      handle',
          '      title',
          '      fitment_flag: metafield(namespace: "fitment", key: "collection") { value }',
          '      metafield(namespace: "fitment", key: "groups") {',
          '        references(first: 250) {',
          '          nodes {',
          '            ... on Metaobject {',
          '              handle',
          '              field(key: "vehicles") {',
          '                references(first: 250) {',
          '                  nodes {',
          '                    ... on Metaobject { handle }',
          '                  }',
          '                }',
          '              }',
          '            }',
          '          }',
          '        }',
          '      }',
          '    }',
          '  }',
          '}'
        ].join('\n');

        _map = fetch(storefrontUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({ query: query })
        })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
          .then(function (d) {
            var result = {};
            // vehicle handle -> [{ handle, title }] of every collection that claims it,
            // in collection-enumeration order.
            var candidates = {};
            _groupMap = {};
            var colls = (d.data && d.data.collections && d.data.collections.nodes) || [];
            colls.forEach(function (coll) {
              if (!coll.fitment_flag || coll.fitment_flag.value !== 'true') return;
              if (!coll.metafield) return;
              var groups = (coll.metafield.references && coll.metafield.references.nodes) || [];
              groups.forEach(function (group) {
                if (!group.field) return;
                var vehicles = (group.field.references && group.field.references.nodes) || [];
                vehicles.forEach(function (v) {
                  if (!v.handle) return;
                  var list = candidates[v.handle] || (candidates[v.handle] = []);
                  // one entry per collection (a collection can reference several groups
                  // that all contain this vehicle — don't double-count it when scoring).
                  if (!list.some(function (x) { return x.handle === coll.handle; })) {
                    list.push({ handle: coll.handle, title: coll.title || '' });
                  }
                  if (group.handle && !_groupMap[v.handle]) _groupMap[v.handle] = group.handle;
                });
              });
            });
            // WHY (conditional routing): map a vehicle to a collection ONLY
            // when the matcher is confident it names the vehicle (make + model + year). Every
            // vehicle is confidence-gated — including single-claim ones, since a vehicle can be
            // claimed solely by a wrong-nameplate sibling via a shared platform group. Vehicles
            // with no confident collection are left UNMAPPED -> forVehicle returns null -> the
            // caller routes to search + fitment facet, never to a wrong page. This also covers
            // the vehicles whose nameplate has no dedicated collection at all.
            Object.keys(candidates).forEach(function (vh) {
              var pick = window.FitmentNameplate.pickCollection(vh, candidates[vh]);
              if (pick) result[vh] = pick;
            });
            return result;
          })
          .catch(function () { _map = null; return {}; });
        return _map;
      },

      /* Returns Promise<string|null> — collection handle for the vehicle, or null */
      forVehicle: function (vehicleHandle, storefrontUrl, token) {
        return this.load(storefrontUrl, token).then(function (map) {
          return map[vehicleHandle] || null;
        });
      },

      /* Returns Promise<string|null> — fitment group handle for the vehicle, or null */
      groupForVehicle: function (vehicleHandle, storefrontUrl, token) {
        return this.load(storefrontUrl, token).then(function () {
          return _groupMap[vehicleHandle] || null;
        });
      }
    };
  }
})();
